import { IsNotEmpty } from 'class-validator';
import { Mapper } from '../../../common/ddd/mapper.base';
import { IOrgWorkingSessionsAnalyticsActivityEnum } from '../../../common';
import { OrgWorkingSessionsEntity } from '../../../core/domain/entities/working-sessions/org-working-sessions.entity';
import { GetSelfWorkingSessionsResponseDto } from '../../../interface/dtos';

export class OrgWorkingSessionsEntityPersistenceResponse {
  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.SessionId]: string;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.LocalSessionStart]: string;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.LocalSessionEnd]: string;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.LocalSessionStartWeek]: string;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.SessionDuration]: number;

  // TODO: Add validation on these fields when values are populated in the db.
  // Currently these values are null but instead of creating a new entity with zero values, return partial data
  [IOrgWorkingSessionsAnalyticsActivityEnum.SessionCallDuration]: number;
  [IOrgWorkingSessionsAnalyticsActivityEnum.SessionCallCount]: number;
  [IOrgWorkingSessionsAnalyticsActivityEnum.SessionFocusDuration]: number;
  [IOrgWorkingSessionsAnalyticsActivityEnum.TotalSessionCallDuration]: number;
  [IOrgWorkingSessionsAnalyticsActivityEnum.UpdatedAt]: string;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.SessionActivityDuration]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.TotalDuration]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.TotalFocusDuration]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.ActivityCount]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.ContextSwitches]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.FocusScore]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.DigitalIntensity]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.FocusPercentage]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.DayCount]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.CallDayCount]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.WeekCount]: number;

  @IsNotEmpty()
  [IOrgWorkingSessionsAnalyticsActivityEnum.CallWeekCount]: number;
}

export class OrgWorkingSessionsEntityMapper
  implements
    Mapper<OrgWorkingSessionsEntity, GetSelfWorkingSessionsResponseDto>
{
  toDomain(
    record: OrgWorkingSessionsEntityPersistenceResponse
  ): OrgWorkingSessionsEntity {
    return OrgWorkingSessionsEntityMapper.toDomain(record);
  }

  static toDomain(
    record: OrgWorkingSessionsEntityPersistenceResponse
  ): OrgWorkingSessionsEntity {
    const entity = OrgWorkingSessionsEntity.create({
      sessionId: String(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.SessionId]
      ),
      startTime: String(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.LocalSessionStart]
      ),
      endTime: String(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.LocalSessionEnd]
      ),
      sessionStartWeek: String(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.LocalSessionStartWeek]
      ),
      duration: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.SessionDuration]
      ),
      sessionCallDuration: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.SessionCallDuration]
      ),
      callCount: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.SessionCallCount]
      ),
      focusDuration: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.SessionFocusDuration]
      ),
      activityDuration: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.SessionActivityDuration]
      ),
      totalDuration: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.TotalDuration]
      ),
      totalSessionCallDuration: Number(
        record[
          IOrgWorkingSessionsAnalyticsActivityEnum.TotalSessionCallDuration
        ]
      ),
      totalActivityDuration: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration]
      ),
      totalFocusDuration: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.TotalFocusDuration]
      ),
      activityCount: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.ActivityCount]
      ),
      contextSwitches: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.ContextSwitches]
      ),
      focusScore: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.FocusScore]
      ),
      digitalIntensity: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.DigitalIntensity]
      ),
      focusPercentage: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.FocusPercentage]
      ),
      dayCount: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.DayCount]
      ),
      callDayCount: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.CallDayCount]
      ),
      weekCount: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.WeekCount]
      ),
      callWeekCount: Number(
        record[IOrgWorkingSessionsAnalyticsActivityEnum.CallWeekCount]
      ),
      updatedAt: record[IOrgWorkingSessionsAnalyticsActivityEnum.UpdatedAt],
    });

    return entity;
  }

  toResponse(
    entity: OrgWorkingSessionsEntity
  ): GetSelfWorkingSessionsResponseDto {
    return OrgWorkingSessionsEntityMapper.toResponse(entity);
  }

  static toResponse(
    entity: OrgWorkingSessionsEntity
  ): GetSelfWorkingSessionsResponseDto {
    return {
      sessionId: entity.sessionId,
      startTime: entity.startTime,
      endTime: entity.endTime,
      sessionStartWeek: entity.sessionStartWeek,
      duration: entity.duration,
      sessionCallDuration: entity.sessionCallDuration,
      callCount: entity.callCount,
      focusDuration: entity.focusDuration,
      activityDuration: entity.activityDuration,
      totalDuration: entity.totalDuration,
      totalSessionCallDuration: entity.totalSessionCallDuration,
      totalActivityDuration: entity.totalActivityDuration,
      totalFocusDuration: entity.totalFocusDuration,
      activityCount: entity.activityCount,
      contextSwitches: entity.contextSwitches,
      focusScore: entity.focusScore,
      digitalIntensity: entity.digitalIntensity,
      focusPercentage: entity.focusPercentage,
      dayCount: entity.dayCount,
      callDayCount: entity.callDayCount,
      weekCount: entity.weekCount,
      callWeekCount: entity.callWeekCount,
      updatedAt: entity.updatedAt.toISOString(),
    };
  }
}
